import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL, Back_url } from "../config";

interface CarPhoto {
  id: number;
  car_id: number;
  path: string;
  is_main: boolean;
  created_at: string;
  updated_at: string;
  url: string;
}

interface Car {
  id: number;
  name: string;
  model: string;
  number: string;
  color: string;
  engine_capacity: string;
  type: string;
  is_available_for_rent: boolean;
  price_per_day: string;
  price_per_month: string;
  price_per_year: string;
  photos: CarPhoto[];
}

interface BookingCar {
  id: number;
  client_info: {
    email: string;
    firstname: string;
    lastname: string;
    phone: string;
    address: string;
  };
  booking_info: {
    booking_date: string;
    end_date: string;
    duration: number;
    rate_type: string;
    new_price: string;
    document_type: string;
    insurance_document: string;
    notes: string;
    status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
    created_at: string;
    updated_at: string;
  };
  car: Car;
}

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgPage: "#f4f6f8",
  bgSubtle: "#f8fafc",
  success: "#059669",
  successBg: "#d1fae5",
  warning: "#d97706",
  warningBg: "#fef3c7",
  danger: "#dc2626",
  dangerBg: "#fee2e2",
  info: "#0284c7",
  infoBg: "#e0f2fe",
};

// ── Skeleton Loader ─────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: colors.bgPage, padding: "20px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Header Skeleton */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <div style={{
              width: 180,
              height: 28,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{
              width: 80,
              height: 24,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 12,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{
            width: 200,
            height: 16,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>
        <div style={{
          width: 130,
          height: 38,
          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
          backgroundSize: "200% 100%",
          borderRadius: 8,
          animation: "shimmer 1.5s infinite"
        }}></div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-lg-8">
          {/* Vehicle Details Skeleton */}
          <div style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            padding: "24px",
            marginBottom: "20px",
          }}>
            <div style={{
              width: 140,
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              marginBottom: 16,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div className="row">
              <div className="col-md-5">
                <div style={{
                  width: "100%",
                  height: 180,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 10,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
              <div className="col-md-7">
                <div style={{
                  width: 150,
                  height: 24,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  marginBottom: 12,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[...Array(5)].map((_, i) => (
                    <div key={i}>
                      <div style={{
                        width: 60,
                        height: 12,
                        background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                        backgroundSize: "200% 100%",
                        borderRadius: 4,
                        marginBottom: 4,
                        animation: "shimmer 1.5s infinite"
                      }}></div>
                      <div style={{
                        width: 80,
                        height: 16,
                        background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                        backgroundSize: "200% 100%",
                        borderRadius: 4,
                        animation: "shimmer 1.5s infinite"
                      }}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Rental Period Skeleton */}
          <div style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            padding: "24px",
          }}>
            <div style={{
              width: 160,
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              marginBottom: 16,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 15 }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{
                  backgroundColor: "#f8fafc",
                  padding: "14px",
                  borderRadius: "10px",
                }}>
                  <div style={{
                    width: 60,
                    height: 12,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    marginBottom: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <div style={{
                    width: 80,
                    height: 18,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4">
          {/* Customer Profile Skeleton */}
          <div style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            padding: "24px",
            marginBottom: "20px",
          }}>
            <div style={{
              width: 130,
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              marginBottom: 16,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{
                width: 45,
                height: 45,
                borderRadius: "50%",
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div>
                <div style={{
                  width: 100,
                  height: 18,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  marginBottom: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                <div style={{
                  width: 80,
                  height: 12,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            </div>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{
                  width: 80,
                  height: 12,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  marginBottom: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                <div style={{
                  width: 120,
                  height: 16,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            ))}
          </div>

          {/* Actions Skeleton */}
          <div style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            padding: "24px",
          }}>
            <div style={{
              width: 140,
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              marginBottom: 16,
              animation: "shimmer 1.5s infinite"
            }}></div>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{
                width: "100%",
                height: 38,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 8,
                marginBottom: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const DetailsRequestRentCar = () => {
  const { id } = useParams<{ id: string }>();
  const [booking, setBooking] = useState<BookingCar | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/booking-cars/${id}`, config_token);
      setBooking(response.data.data);
    } catch (error) {
      console.error("Error loading details:", error);
      toast.error("Failed to load booking details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchBookingDetails();
    }
  }, [id]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (price: string) => {
    if (!price) return "0.000 OMR";
    const num = parseFloat(price);
    return `${num.toFixed(3)} OMR`;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; color: string; text: string }> = {
      pending: { bg: colors.warningBg, color: colors.warning, text: "Pending" },
      confirmed: { bg: colors.successBg, color: colors.success, text: "Confirmed" },
      cancelled: { bg: colors.dangerBg, color: colors.danger, text: "Cancelled" },
      completed: { bg: colors.infoBg, color: colors.info, text: "Completed" }
    };

    const config = statusConfig[status] || { bg: "#f1f5f9", color: "#475569", text: status };
    
    return (
      <span style={{
        backgroundColor: config.bg,
        color: config.color,
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px"
      }}>
        <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: config.color }}></span>
        {config.text}
      </span>
    );
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!booking) return;
    if (booking.booking_info.status === newStatus) return;

    try {
      setUpdating(true);
      await axios.put(`${API_URL}/booking-cars/${id}`, { 
        status: newStatus 
      }, config_token);
      
      toast.success(`Status updated to ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`);
      fetchBookingDetails();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");
    } finally {
      setUpdating(false);
    }
  };

  const StatusButton = ({ status, label, icon, bgColor, textColor }: { 
    status: string; 
    label: string; 
    icon: string; 
    bgColor: string; 
    textColor: string;
  }) => {
    const isActive = booking?.booking_info.status === status;
    const isDisabled = isActive || updating;

    return (
      <button
        onClick={() => handleStatusUpdate(status)}
        disabled={isDisabled}
        style={{
          padding: "10px 16px",
          borderRadius: "8px",
          border: isActive ? `2px solid ${textColor}` : "none",
          fontWeight: 600,
          fontSize: "13.5px",
          width: "100%",
          cursor: isDisabled ? "not-allowed" : "pointer",
          backgroundColor: isActive ? `${bgColor}20` : bgColor,
          color: isActive ? textColor : textColor,
          opacity: isDisabled ? 0.7 : 1,
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
        onMouseOver={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.opacity = "0.8";
          }
        }}
        onMouseOut={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.opacity = "1";
          }
        }}
      >
        <span>{icon}</span>
        {label} {isActive && "✓"}
      </button>
    );
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!booking) {
    return (
      <div style={{ width: "100%", minHeight: "100vh", backgroundColor: colors.bgPage, padding: "40px", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ textAlign: "center" }}>
          <i className="fa-solid fa-car" style={{ fontSize: "48px", color: "#cbd5e1" }}></i>
          <h2 style={{ color: colors.text, marginTop: "16px" }}>Booking not found</h2>
          <p style={{ color: colors.textMuted }}>The booking you're looking for doesn't exist.</p>
          <button
            onClick={() => window.history.back()}
            style={{
              marginTop: "16px",
              padding: "10px 24px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: colors.primary,
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ← Back to List
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: colors.bgPage,
          padding: "20px 20px",
          boxSizing: "border-box",
          color: colors.text,
        }}
      >
        <ToastContainer position="top-right" autoClose={3000} />

        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          {/* SECTION EN-TÊTE */}
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h1
                  style={{
                    color: colors.primary,
                    fontSize: "22px",
                    fontWeight: 700,
                    margin: 0,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Booking #{booking.id}
                </h1>
                {getStatusBadge(booking.booking_info.status)}
              </div>
              <p
                style={{
                  color: "#64748b",
                  fontSize: "13.5px",
                  margin: 0,
                  fontWeight: 400,
                }}
              >
                Created on {formatDate(booking.booking_info.created_at)}
              </p>
            </div>

            <button
              type="button"
              onClick={() => window.history.back()}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#475569",
                fontSize: "13.5px",
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.15s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.color = "#0f172a";
                e.currentTarget.style.borderColor = "#94a3b8";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.color = "#475569";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
            >
              <i className="fa-solid fa-arrow-left"></i>
              Back to List
            </button>
          </div>

          <div className="row g-4">
            {/* Colonne Gauche (8) */}
            <div className="col-12 col-lg-8">
              {/* Vehicle Details */}
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  padding: "24px",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ fontSize: "15px", fontWeight: 700, color: colors.primary, margin: "0 0 16px 0", letterSpacing: "-0.01em" }}>
                  Vehicle Details
                </h3>

                <div className="row g-3 align-items-start">
                  <div className="col-md-4">
                    {booking.car.photos.length > 0 ? (
                      <img
                        src={`${Back_url}/${booking.car.photos.find(p => p.is_main)?.path || booking.car.photos[0].path}`}
                        alt={booking.car.name}
                        style={{
                          width: "100%",
                          height: "160px",
                          objectFit: "cover",
                          borderRadius: "10px",
                          border: "1px solid #f1f5f9",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "160px",
                          backgroundColor: "#f1f5f9",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#94a3b8",
                          fontSize: "13px",
                        }}
                      >
                        <i className="fa-solid fa-car" style={{ fontSize: "32px", marginRight: "8px" }}></i>
                        No Image
                      </div>
                    )}
                  </div>
                  <div className="col-md-8">
                    <h4 style={{ fontSize: "18px", fontWeight: 700, color: "#0f172a", marginBottom: "8px" }}>
                      {booking.car.name}
                    </h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: "13.5px" }}>
                      <div>
                        <span style={{ color: "#64748b" }}>Model:</span>{' '}
                        <strong style={{ color: "#0f172a" }}>{booking.car.model}</strong>
                      </div>
                      <div>
                        <span style={{ color: "#64748b" }}>Color:</span>{' '}
                        <strong style={{ color: "#0f172a" }}>{booking.car.color}</strong>
                      </div>
                      <div>
                        <span style={{ color: "#64748b" }}>Gearbox:</span>{' '}
                        <strong style={{ color: "#0f172a", textTransform: "capitalize" }}>{booking.car.type}</strong>
                      </div>
                      <div>
                        <span style={{ color: "#64748b" }}>Engine:</span>{' '}
                        <strong style={{ color: "#0f172a" }}>{booking.car.engine_capacity}</strong>
                      </div>
                      <div>
                        <span style={{ color: "#64748b" }}>Plate:</span>{' '}
                        <strong style={{ color: "#0f172a" }}>{booking.car.number}</strong>
                      </div>
                      <div>
                        <span style={{ color: "#64748b" }}>Status:</span>{' '}
                        <span
                          style={{
                            fontWeight: 600,
                            color: booking.car.is_available_for_rent ? colors.success : colors.danger,
                          }}
                        >
                          {booking.car.is_available_for_rent ? "Available" : "Unavailable"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Thumbnails */}
                {booking.car.photos.length > 1 && (
                  <div style={{ display: "flex", gap: "8px", marginTop: "14px", overflowX: "auto", paddingBottom: "4px" }}>
                    {booking.car.photos.map((photo) => (
                      <img
                        key={photo.id}
                        src={`${Back_url}/${photo.path}`}
                        alt="car thumbnail"
                        style={{
                          width: "60px",
                          height: "44px",
                          objectFit: "cover",
                          borderRadius: "6px",
                          border: "1px solid #e2e8f0",
                          flexShrink: 0,
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Rental Period & Pricing */}
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  padding: "24px",
                }}
              >
                <h3 style={{ fontSize: "15px", fontWeight: 700, color: colors.primary, margin: "0 0 16px 0", letterSpacing: "-0.01em" }}>
                  Rental Period & Pricing
                </h3>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px" }}>
                  <div
                    style={{
                      backgroundColor: "#f8fafc",
                      padding: "14px 16px",
                      borderRadius: "10px",
                      border: "1px solid #f1f5f9",
                    }}
                  >
                    <div style={{ fontSize: "11.5px", fontWeight: 600, color: "#94a3b8",  letterSpacing: "0.04em" }}>
                      Start Date
                    </div>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", marginTop: "2px" }}>
                      {formatDate(booking.booking_info.booking_date)}
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: "#f8fafc",
                      padding: "14px 16px",
                      borderRadius: "10px",
                      border: "1px solid #f1f5f9",
                    }}
                  >
                    <div style={{ fontSize: "11.5px", fontWeight: 600, color: "#94a3b8",  letterSpacing: "0.04em" }}>
                      End Date
                    </div>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", marginTop: "2px" }}>
                      {formatDate(booking.booking_info.end_date)}
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: "#f8fafc",
                      padding: "14px 16px",
                      borderRadius: "10px",
                      border: "1px solid #f1f5f9",
                    }}
                  >
                    <div style={{ fontSize: "11.5px", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.04em" }}>
                      Duration
                    </div>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", marginTop: "2px" }}>
                      {booking.booking_info.duration} Days
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: colors.successBg,
                      padding: "14px 16px",
                      borderRadius: "10px",
                      border: `1px solid ${colors.success}30`,
                    }}
                  >
                    <div style={{ fontSize: "11.5px", fontWeight: 600, color: colors.success,  letterSpacing: "0.04em" }}>
                      Total Amount
                    </div>
                    <div style={{ fontSize: "18px", fontWeight: 700, color: colors.success, marginTop: "2px" }}>
                      {formatPrice(booking.booking_info.new_price)}
                    </div>
                  </div>
                </div>

                {booking.booking_info.notes && (
                  <div
                    style={{
                      marginTop: "14px",
                      backgroundColor: "#fffbeb",
                      border: "1px solid #fde68a",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#92400e",
                    }}
                  >
                    <strong>Notes:</strong> {booking.booking_info.notes}
                  </div>
                )}
              </div>
            </div>

            {/* Colonne Droite (4) */}
            <div className="col-12 col-lg-4">
              {/* Customer Profile */}
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  padding: "24px",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ fontSize: "15px", fontWeight: 700, color: colors.primary, margin: "0 0 16px 0", letterSpacing: "-0.01em" }}>
                  Customer Profile
                </h3>

                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      backgroundColor: colors.primary,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "16px",
                      flexShrink: 0,
                    }}
                  >
                    {booking.client_info.firstname.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a" }}>
                      {booking.client_info.firstname} {booking.client_info.lastname}
                    </div>
                    <span style={{ fontSize: "12px", color: colors.success, fontWeight: 500 }}>
                      <i className="fa-solid fa-check-circle" style={{ fontSize: "11px", marginRight: "4px" }}></i>
                      Verified Customer
                    </span>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px" }}>
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.04em" }}>
                      Email Address
                    </div>
                    <a
                      href={`mailto:${booking.client_info.email}`}
                      style={{ color: colors.primary, textDecoration: "none", fontWeight: 500 }}
                    >
                      {booking.client_info.email}
                    </a>
                  </div>
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.04em" }}>
                      Phone Number
                    </div>
                    <a
                      href={`tel:${booking.client_info.phone}`}
                      style={{ color: "#0f172a", textDecoration: "none", fontWeight: 500 }}
                    >
                      {booking.client_info.phone}
                    </a>
                  </div>
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8",  letterSpacing: "0.04em" }}>
                      Location / Country
                    </div>
                    <div style={{ color: "#0f172a", fontWeight: 500 }}>{booking.client_info.address}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8",  letterSpacing: "0.04em" }}>
                      Document Type
                    </div>
                    <div style={{ color: "#0f172a", fontWeight: 500 }}>
                      {booking.booking_info.document_type?.replace(/_/g, ' ')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Manage Booking Status */}
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  padding: "24px",
                }}
              >
                <h3 style={{ fontSize: "15px", fontWeight: 700, color: colors.primary, margin: "0 0 16px 0", letterSpacing: "-0.01em" }}>
                  Manage Booking Status
                </h3>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <StatusButton
                    status="confirmed"
                    label="Confirm Booking"
                    icon="✓"
                    bgColor={colors.successBg}
                    textColor={colors.success}
                  />
                  <StatusButton
                    status="completed"
                    label="Mark as Completed"
                    icon="★"
                    bgColor={colors.infoBg}
                    textColor={colors.info}
                  />
                  <StatusButton
                    status="pending"
                    label="Set to Pending"
                    icon="⏳"
                    bgColor={colors.warningBg}
                    textColor={colors.warning}
                  />
                  <StatusButton
                    status="cancelled"
                    label="Cancel Booking"
                    icon="✕"
                    bgColor={colors.dangerBg}
                    textColor={colors.danger}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailsRequestRentCar;